import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import type { DiscordWebhook } from '../../types/discord';
import { updateWebhook, deleteWebhook, testWebhook } from '../../api/discord';
import { TestWebhookModal } from './TestWebhookModal';

interface Props {
  webhook: DiscordWebhook;
  canWrite: boolean;
  onChanged: () => void;
}

export function WebhookCard({ webhook, canWrite, onChanged }: Props) {
  const [testOpen, setTestOpen] = useState(false);

  async function handleToggle() {
    await updateWebhook(webhook.id, { active: !webhook.active });
    onChanged();
  }

  async function handleDelete() {
    if (!confirm(`Delete webhook "${webhook.name}"? Templates using it will also be removed.`)) return;
    await deleteWebhook(webhook.id);
    onChanged();
  }

  return (
    <>
      <Card variant="outlined" sx={{ opacity: webhook.active ? 1 : 0.6 }}>
        <CardContent>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box flex={1} minWidth={0}>
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {webhook.name}
                </Typography>
                <Chip
                  label={webhook.active ? 'Active' : 'Inactive'}
                  size="small"
                  color={webhook.active ? 'success' : 'default'}
                />
              </Stack>
              <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.5 }}>
                {webhook.channel} · {webhook.server}
              </Typography>
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ fontFamily: 'monospace', display: 'block', mt: 0.5 }}
                noWrap
              >
                {webhook.url}
              </Typography>
            </Box>
            {canWrite && (
              <Stack direction="row" spacing={0.5} flexShrink={0}>
                <Tooltip title="Send test message">
                  <IconButton size="small" onClick={() => setTestOpen(true)}>
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={webhook.active ? 'Disable' : 'Enable'}>
                  <IconButton size="small" onClick={handleToggle}>
                    {webhook.active ? (
                      <ToggleOnIcon fontSize="small" color="success" />
                    ) : (
                      <ToggleOffIcon fontSize="small" />
                    )}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete webhook">
                  <IconButton size="small" onClick={handleDelete} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          </Stack>
        </CardContent>
      </Card>
      <TestWebhookModal
        open={testOpen}
        webhook={webhook}
        onClose={() => setTestOpen(false)}
        testFn={(content) => testWebhook(webhook.id, content)}
      />
    </>
  );
}
